import { useCallback, useState } from 'react';
import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { useTranslation } from 'react-i18next';
import {
    ButtonPropsModal,
    ItemsFooterType,
} from '@celebration/react/ClbModal/ClbModal.types';
import IApproveUserModal from './IApproveUserModal';
import requestsTableService from '../../services/requests-table.service';
import { useToast } from '../../../../../../app/contexts/toast/toast.context';
import { SULAHUB_USERS_BY_USER_TYPE } from '../../../../../../common/constants/roles';

function ApproveUserModal({
    userToApprove,
    approveModalOpen,
    onApproveModalClose,
    fetchData,
}: Readonly<IApproveUserModal>) {
    const { t } = useTranslation();

    const { setToast } = useToast();

    const [isLoading, setIsLoading] = useState(false);

    const handleApproveModalClose = useCallback(
        () => onApproveModalClose(),
        [onApproveModalClose],
    );

    const closeApproveModalHandler = useCallback(() => {
        if (!isLoading) {
            handleApproveModalClose();
        }
    }, [isLoading, handleApproveModalClose]);

    const cancelReproveButton: ButtonPropsModal = {
        id: 'cancel-reprove-button',
        label: t('Cancelar'),
        disabled: isLoading,
        loading: false,
        title: t('CancelarReprovacaoUsuario'),
        'data-testid': 'modal-back-button',
        onClick: () => handleApproveModalClose(),
        role: 'button',
        size: 'md',
        styleType: 'secondary',
    };

    const confirmReproveButton: ButtonPropsModal = {
        id: 'confirm-reprove-button',
        label: t('Confirmar'),
        disabled: false,
        loading: isLoading,
        title: t('ConfirmarReprovacaoUsuario'),
        'data-testid': 'modal-confirm-button',
        onClick: () => {
            setIsLoading(true);

            requestsTableService
                .approveUserRequest(
                    userToApprove.userId,
                    userToApprove.userType,
                )
                .then(() => {
                    setToast('positive', 'Sucesso', t('UsuarioAprovado'));
                })
                .catch(err => {
                    setToast('negative', 'Erro', err.message);
                })
                .finally(() => {
                    onApproveModalClose();
                    setIsLoading(false);
                    fetchData();
                });
        },
        role: 'button',
        size: 'md',
    };

    const buttonsApproveModal: ItemsFooterType = [
        confirmReproveButton,
        cancelReproveButton,
    ];

    return (
        <ClbModal
            title={t('ConfirmarAprovacao')}
            open={approveModalOpen}
            onClose={closeApproveModalHandler}
            description={`${
                t('ConfirmacaoApovacaoUsuario') +
                (userToApprove.userType === undefined
                    ? userToApprove.userTypeLabel
                    : // eslint-disable-next-line no-unsafe-optional-chaining
                      SULAHUB_USERS_BY_USER_TYPE.find(
                          type => type.value === userToApprove.userType,
                      )?.label)
            }?`}
            type="default"
            itemsFooter={buttonsApproveModal}
            data-testid="approve-user-modal"
        />
    );
}

export default ApproveUserModal;
